import styled from '@emotion/styled';
import * as tokens from '@tokens';
import { Icon } from '@components/Icon/Icon';
import { Select } from '@components/Inputs/Select/Select';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Button = styled.button<{ active?: boolean }>`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
  border: none;
  border-bottom: 1px solid transparent;
  background: none;
  cursor: pointer;

  ${(p) =>
    p.active &&
    `
    &,
    &:hover {
      border-bottom-color: ${tokens.color.text_default};
      color: ${tokens.color.text_default};
      font-weight: 600;
    }
  `}

  &:hover,
  &:focus {
    color: ${tokens.color.link};
  }

  &:focus {
    border-bottom-color: transparent;
    outline: none;
    border-radius: ${tokens.radius.m};
    ${tokens.focus()};
  }
`;

const PrevButton = styled(Button)`
  margin-right: ${tokens.spacer.x1};
`;

const NextButton = styled(Button)`
  margin-left: ${tokens.spacer.x1};
`;

const PageButton = styled(Button)`
  margin: 2px;
`;

const Label = styled.label`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  text-transform: none; // temp solution until the old styles from IWA are removed
  font-weight: normal;

  display: flex;
  align-items: center;
  margin: 0;
  padding: 0 0 0 ${tokens.spacer.x2};
`;

const LabelText = styled.div`
  margin-left: ${tokens.spacer.x1};
`;

const Spacer = styled.div`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  padding: ${tokens.spacer.x0} ${tokens.spacer.x1};
`;

const PAGE_SIZES = [30, 50, 100];
const PAGINATION_SIZE = 5;

export interface PaginationProps {
  /**
   * Total number of items
   */
  totalCount: number;
  /**
   * Current page being shown
   */
  currentPage: number;
  /**
   * Should the Pagination component show first and last page as well
   */
  withSpacer?: boolean;
  /**
   * Callback when the page has changed
   */
  onPageChange: (page: number) => void;
  /**
   * Number of items on a page
   */
  pageSize: number;
  /**
   * Callback when the page size has changed
   */
  onPageSizeChange: (pageSize: number) => void;
  /**
   * Custom `Items per page` label
   */
  itemsPerPageLabel?: string;
  /**
   * Custom size option values
   */
  pageSizes?: number[];
}

/**
 * Pagination component
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/49247e-pagination Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-pagination--basic-pagination Code documentation}
 *
 * @example
 * <Pagination totalCount={300} currentPage={1} pageSize={30} onPageChange={{} => ()} onPageSizeChange={{} => ()} />
 */
export function Pagination({
  totalCount,
  currentPage,
  withSpacer = false,
  onPageChange,
  pageSize,
  onPageSizeChange,
  itemsPerPageLabel,
  pageSizes = PAGE_SIZES,
}: PaginationProps): JSX.Element {
  const { pagination } = useStrings();
  const interpolate = useInterpolate();

  const finalItemsPerPageLabel = itemsPerPageLabel || pagination.itemsPerPage;

  const totalPages = Math.ceil(totalCount / pageSize);

  const start = Math.max(currentPage - Math.floor(PAGINATION_SIZE / 2), 1);
  const end = start + PAGINATION_SIZE;
  const firstPage = end > totalPages ? start - (end - totalPages - 1) : start;
  const pages = [...Array(PAGINATION_SIZE)]
    .map((_, index) => index + firstPage)
    .filter((page) => page > 0);

  const showFirstPage = withSpacer && pages[0] > 1;
  const showLastPage = withSpacer && pages[pages.length - 2] < totalPages - 1;

  function changePage(newPage: number) {
    onPageChange(Math.max(newPage, 1));
  }

  function changePageSize(newPageSize: string) {
    onPageSizeChange(parseInt(newPageSize, 10));
    changePage(1);
  }

  function renderPageButton(page: number, key?: number) {
    return (
      <PageButton
        key={key}
        type="button"
        title={interpolate(pagination.goToPage, { page })}
        active={page === currentPage}
        onClick={() => changePage(page)}
      >
        {page}
      </PageButton>
    );
  }

  return (
    <Wrapper>
      <PrevButton
        type="button"
        title={pagination.previousPageButtonTitle}
        onClick={() => changePage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <Icon icon="chevron_left" />
        {pagination.previousPageButtonDisplayMessage}
      </PrevButton>
      {showFirstPage && (
        <>
          {renderPageButton(1)}
          <Spacer>…</Spacer>
        </>
      )}
      {pages.map((page, index) => renderPageButton(page, index))}
      {showLastPage && (
        <>
          <Spacer>…</Spacer>
          {renderPageButton(totalPages)}
        </>
      )}
      <NextButton
        type="button"
        title={pagination.nextPageButtonTitle}
        onClick={() => changePage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        {pagination.nextPageButtonDisplayMessage}
        <Icon icon="chevron_right" />
      </NextButton>
      <Label>
        <Select
          size="s"
          onChange={(ev) => changePageSize(ev.currentTarget.value)}
          value={pageSize}
        >
          {pageSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
        <LabelText>{finalItemsPerPageLabel}</LabelText>
      </Label>
    </Wrapper>
  );
}
